import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FiShoppingBag, FiCheck, FiX, FiClock, FiPackage, FiAlertCircle, FiSearch, FiUser, FiPlus, FiEye, FiArrowLeft } from "react-icons/fi";
import Invoice from "../components/Invoice";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

  .ms-root {
    font-family: 'Montserrat', sans-serif;
    min-height: 100vh;
    background: linear-gradient(135deg, #e0e5ec 0%, #d1d6df 100%);
    padding: 120px 16px 48px;
  }

  .ms-top-nav {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
    padding: 12px 24px;
    z-index: 1000;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .ms-goto-member-btn-top {
    padding: 10px 20px;
    background: rgba(255, 255, 255, 0.15);
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 10px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: 'Montserrat', sans-serif;
    backdrop-filter: blur(10px);
  }

  .ms-goto-member-btn-top:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  .ms-root *, .ms-root *::before, .ms-root *::after { box-sizing: border-box; }

  .ms-container {
    max-width: 80rem;
    margin: 0 auto;
  }

  .ms-header {
    margin-bottom: 32px;
  }

  .ms-title {
    font-size: 28px;
    font-weight: 800;
    background: linear-gradient(135deg, #1e3a5f, #2563eb);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .ms-subtitle {
    color: #64748b;
    margin-top: 8px;
    font-weight: 500;
  }

  .ms-alert-error {
    margin-bottom: 24px;
    padding: 16px;
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    border-radius: 12px;
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .ms-alert-success {
    margin-bottom: 24px;
    padding: 16px;
    background: rgba(16, 185, 129, 0.1);
    border: 1px solid rgba(16, 185, 129, 0.3);
    border-radius: 12px;
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .ms-alert-icon {
    margin-top: 2px;
    flex-shrink: 0;
  }

  .ms-alert-error .ms-alert-icon { color: #dc2626; }
  .ms-alert-success .ms-alert-icon { color: #16a34a; }

  .ms-alert-text {
    font-size: 14px;
    font-weight: 600;
  }
  .ms-alert-error .ms-alert-text { color: #b91c1c; }
  .ms-alert-success .ms-alert-text { color: #15803d; }

  /* Tabs */
  .ms-tabs {
    display: flex;
    gap: 16px;
    margin-bottom: 32px;
  }

  .ms-tab {
    padding: 12px 24px;
    border-radius: 12px;
    font-weight: 700;
    font-size: 14px;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    color: #64748b;
    box-shadow: 6px 6px 12px #8f95a0, -3px -3px 8px #ffffff;
  }

  .ms-tab:hover {
    transform: translateY(-2px);
  }

  .ms-tab-active {
    background: linear-gradient(135deg, #2563eb, #3b82f6);
    color: #ffffff;
    box-shadow: 8px 8px 16px #8f95a0, -4px -4px 8px #ffffff;
  }

  .ms-tab-inactive {
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    color: #64748b;
  }

  /* Stats Grid */
  .ms-stats-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 32px;
  }

  @media (min-width: 768px) {
    .ms-stats-grid {
      grid-template-columns: repeat(4, 1fr);
    }
  }

  .ms-stat-card {
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    border-radius: 16px;
    padding: 20px 24px;
    box-shadow: 10px 10px 20px #8f95a0, -5px -5px 12px #ffffff;
    transition: all 0.3s ease;
  }

  .ms-stat-card:hover {
    transform: translateY(-4px);
    box-shadow: 14px 14px 24px #8f95a0, -6px -6px 14px #ffffff;
  }

  .ms-stat-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .ms-stat-label {
    font-size: 13px;
    font-weight: 600;
    color: #64748b;
  }

  .ms-stat-value {
    font-size: 28px;
    font-weight: 900;
    color: #1e293b;
  }

  .ms-stat-value-blue { color: #2563eb; }
  .ms-stat-value-yellow { color: #ca8a04; }
  .ms-stat-value-green { color: #16a34a; }
  .ms-stat-value-red { color: #dc2626; }

  .ms-stat-icon {
    font-size: 32px;
  }

  /* Table */
  .ms-table-container {
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 10px 10px 20px #8f95a0, -5px -5px 12px #ffffff;
  }

  .ms-table-header {
    padding: 16px 24px;
    border-bottom: 1px solid rgba(0,0,0,0.08);
  }

  .ms-table-title {
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
  }

  .ms-empty-state {
    padding: 48px;
    text-align: center;
  }

  .ms-empty-icon {
    margin: 0 auto 16px;
    color: #94a3b8;
  }

  .ms-empty-text {
    color: #64748b;
    font-weight: 500;
  }

  .ms-table-wrapper {
    overflow-x: auto;
  }

  .ms-table {
    width: 100%;
    border-collapse: collapse;
  }

  .ms-table-head {
    background: rgba(0,0,0,0.04);
  }

  .ms-table-head-cell {
    padding: 12px 16px;
    text-align: left;
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .ms-table-body tr {
    border-bottom: 1px solid rgba(0,0,0,0.06);
    transition: background 0.2s;
  }

  .ms-table-body tr:hover {
    background: rgba(37, 99, 235, 0.05);
  }

  .ms-table-cell {
    padding: 16px;
    font-size: 14px;
    color: #1e293b;
  }

  .ms-table-cell-amount {
    font-weight: 700;
  }

  .ms-customer-name {
    font-weight: 600;
    color: #1e293b;
  }

  .ms-customer-mobile {
    font-size: 12px;
    color: #64748b;
  }

  .ms-no-invoice {
    color: #94a3b8;
    font-style: italic;
  }

  /* Badges */
  .ms-badge {
    padding: 4px 14px;
    border-radius: 9999px;
    font-size: 12px;
    font-weight: 700;
    display: inline-block;
  }

  .ms-badge-pending {
    background: rgba(234, 179, 8, 0.15);
    color: #854d0e;
  }

  .ms-badge-processing {
    background: rgba(37, 99, 235, 0.15);
    color: #1e40af;
  }

  .ms-badge-delivered {
    background: rgba(16, 185, 129, 0.15);
    color: #065f46;
  }

  .ms-badge-cancelled {
    background: rgba(239, 68, 68, 0.15);
    color: #991b1b;
  }

  .ms-badge-default {
    background: rgba(0,0,0,0.06);
    color: #374151;
  }

  /* Action Buttons */
  .ms-actions {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }

  .ms-btn {
    padding: 4px 12px;
    border: none;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .ms-btn:hover {
    transform: translateY(-1px);
  }

  .ms-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  .ms-btn-purple {
    background: linear-gradient(135deg, #7c3aed, #8b5cf6);
    color: #ffffff;
  }

  .ms-btn-purple:hover:not(:disabled) {
    box-shadow: 0 4px 12px rgba(124, 58, 237, 0.4);
  }

  .ms-btn-green {
    background: linear-gradient(135deg, #059669, #10b981);
    color: #ffffff;
  }

  .ms-btn-green:hover:not(:disabled) {
    box-shadow: 0 4px 12px rgba(5, 150, 105, 0.4);
  }

  .ms-btn-red {
    background: linear-gradient(135deg, #dc2626, #ef4444);
    color: #ffffff;
  }

  .ms-btn-red:hover:not(:disabled) {
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.4);
  }

  .ms-btn-blue {
    background: linear-gradient(135deg, #2563eb, #3b82f6);
    color: #ffffff;
  }

  .ms-btn-blue:hover:not(:disabled) {
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.4);
  }

  .ms-completed {
    color: #16a34a;
    font-weight: 700;
  }

  .ms-cancelled-text {
    color: #dc2626;
    font-weight: 700;
  }

  /* Offline Section */
  .ms-offline-container {
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    border-radius: 16px;
    padding: 24px;
    box-shadow: 10px 10px 20px #8f95a0, -5px -5px 12px #ffffff;
  }

  .ms-offline-title {
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 24px;
  }

  .ms-search-section {
    margin-bottom: 24px;
    padding: 16px;
    background: linear-gradient(145deg, #f0f4f9, #e8edf5);
    border-radius: 12px;
    box-shadow: inset 3px 3px 8px rgba(0,0,0,0.06), inset -3px -3px 8px rgba(255,255,255,0.6);
  }

  .ms-search-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 12px;
    font-size: 15px;
  }

  .ms-search-group {
    display: flex;
    gap: 12px;
  }

  .ms-search-input {
    flex: 1;
    padding: 10px 16px;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    box-shadow: inset 3px 3px 8px rgba(0,0,0,0.08), inset -3px -3px 8px rgba(255,255,255,0.6);
    font-family: 'Montserrat', sans-serif;
    outline: none;
    transition: all 0.2s;
  }

  .ms-search-input:focus {
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2), inset 3px 3px 8px rgba(0,0,0,0.08);
  }

  .ms-search-btn {
    padding: 10px 24px;
    background: linear-gradient(135deg, #2563eb, #3b82f6);
    color: #ffffff;
    border: none;
    border-radius: 10px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: 'Montserrat', sans-serif;
  }

  .ms-search-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(37, 99, 235, 0.4);
  }

  .ms-search-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .ms-user-details {
    margin-bottom: 24px;
    padding: 16px;
    background: rgba(16, 185, 129, 0.08);
    border: 1px solid rgba(16, 185, 129, 0.2);
    border-radius: 12px;
  }

  .ms-user-details-title {
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 12px;
    font-size: 15px;
  }

  .ms-user-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 16px;
    font-size: 14px;
  }

  .ms-user-grid p {
    margin: 4px 0;
  }

  .ms-user-grid strong {
    color: #1e293b;
  }

  .ms-purchase-btn {
    margin-top: 12px;
    padding: 8px 24px;
    background: linear-gradient(135deg, #059669, #10b981);
    color: #ffffff;
    border: none;
    border-radius: 10px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: 'Montserrat', sans-serif;
  }

  .ms-purchase-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(5, 150, 105, 0.4);
  }

  .ms-products-section {
    margin-top: 8px;
  }

  .ms-products-title {
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 12px;
    font-size: 15px;
  }

  .ms-loading-text {
    text-align: center;
    padding: 32px;
    color: #64748b;
  }

  .ms-products-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (min-width: 768px) {
    .ms-products-grid {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  .ms-product-card {
    padding: 16px;
    background: linear-gradient(145deg, #f0f4f9, #e8edf5);
    border-radius: 12px;
    box-shadow: inset 2px 2px 6px rgba(0,0,0,0.06), inset -2px -2px 6px rgba(255,255,255,0.6);
    transition: all 0.3s ease;
  }

  .ms-product-card:hover {
    transform: translateY(-2px);
    box-shadow: 6px 6px 12px #8f95a0, -3px -3px 8px #ffffff;
  }

  .ms-product-name {
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 8px;
  }

  .ms-product-detail {
    font-size: 13px;
    color: #64748b;
    margin: 2px 0;
  }

  /* Modals */
  .ms-modal-overlay {
    position: fixed;
    inset: 0;
    margin-top: 120px;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    padding: 16px;
  }

  .ms-modal {
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    border-radius: 20px;
    padding: 24px;
    width: 100%;
    max-width: 480px;
    position: relative;
    box-shadow: 16px 16px 32px #8f95a0, -8px -8px 16px #ffffff;
    max-height: 90vh;
    overflow-y: auto;
  }

  .ms-modal-close {
    position: absolute;
    top: 12px;
    right: 16px;
    font-size: 28px;
    background: none;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    transition: color 0.2s;
    font-family: 'Montserrat', sans-serif;
  }

  .ms-modal-close:hover {
    color: #1e293b;
  }

  .ms-modal-title {
    font-size: 20px;
    font-weight: 800;
    margin-bottom: 16px;
  }

  .ms-modal-title-confirm { color: #1e293b; }
  .ms-modal-title-reject { color: #dc2626; }

  .ms-modal-details {
    padding: 16px;
    background: linear-gradient(145deg, #f0f4f9, #e8edf5);
    border-radius: 12px;
    margin-bottom: 16px;
    box-shadow: inset 2px 2px 6px rgba(0,0,0,0.06), inset -2px -2px 6px rgba(255,255,255,0.6);
  }

  .ms-modal-details p {
    font-size: 14px;
    margin: 4px 0;
    color: #1e293b;
  }

  .ms-modal-details strong {
    color: #1e293b;
  }

  .ms-form-group {
    margin-bottom: 16px;
  }

  .ms-form-label {
    display: block;
    font-size: 13px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 6px;
  }

  .ms-form-input {
    width: 100%;
    padding: 10px 14px;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    box-shadow: inset 3px 3px 8px rgba(0,0,0,0.08), inset -3px -3px 8px rgba(255,255,255,0.6);
    font-family: 'Montserrat', sans-serif;
    outline: none;
    transition: all 0.2s;
  }

  .ms-form-input:focus {
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2), inset 3px 3px 8px rgba(0,0,0,0.08);
  }

  .ms-form-textarea {
    width: 100%;
    padding: 10px 14px;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    box-shadow: inset 3px 3px 8px rgba(0,0,0,0.08), inset -3px -3px 8px rgba(255,255,255,0.6);
    font-family: 'Montserrat', sans-serif;
    outline: none;
    resize: vertical;
    transition: all 0.2s;
  }

  .ms-form-textarea:focus {
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2), inset 3px 3px 8px rgba(0,0,0,0.08);
  }

  .ms-form-select {
    width: 100%;
    padding: 10px 14px;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    box-shadow: inset 3px 3px 8px rgba(0,0,0,0.08), inset -3px -3px 8px rgba(255,255,255,0.6);
    font-family: 'Montserrat', sans-serif;
    outline: none;
    cursor: pointer;
    appearance: none;
    -webkit-appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%232563eb' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
  }

  .ms-form-select:focus {
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.2), inset 3px 3px 8px rgba(0,0,0,0.08);
  }

  .ms-quantity-info {
    font-size: 13px;
    color: #64748b;
    margin-top: 4px;
  }

  .ms-rejection-note {
    font-size: 13px;
    color: #dc2626;
    margin-bottom: 12px;
    padding: 8px 12px;
    background: rgba(239, 68, 68, 0.08);
    border-radius: 8px;
  }

  .ms-modal-btn {
    width: 100%;
    padding: 12px;
    border: none;
    border-radius: 12px;
    font-weight: 700;
    font-size: 15px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: 'Montserrat', sans-serif;
  }

  .ms-modal-btn:hover:not(:disabled) {
    transform: translateY(-2px);
  }

  .ms-modal-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  .ms-modal-btn-green {
    background: linear-gradient(135deg, #059669, #10b981);
    color: #ffffff;
  }

  .ms-modal-btn-green:hover:not(:disabled) {
    box-shadow: 0 4px 16px rgba(5, 150, 105, 0.4);
  }

  .ms-modal-btn-red {
    background: linear-gradient(135deg, #dc2626, #ef4444);
    color: #ffffff;
  }

  .ms-modal-btn-red:hover:not(:disabled) {
    box-shadow: 0 4px 16px rgba(220, 38, 38, 0.4);
  }

  /* Skeleton Loading */
  .ms-skeleton {
    min-height: 100vh;
    background: #e0e5ec;
    padding: 120px 16px 48px;
  }

  .ms-skeleton-inner {
    max-width: 80rem;
    margin: 0 auto;
  }

  .ms-skeleton-title {
    height: 48px;
    background: linear-gradient(145deg, #d1d6df, #e0e5ec);
    border-radius: 12px;
    margin-bottom: 16px;
    animation: ms-shimmer 1.4s infinite;
    background-size: 200% 100%;
  }

  .ms-skeleton-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }

  @media (max-width: 768px) {
    .ms-skeleton-grid {
      grid-template-columns: 1fr 1fr;
    }
  }

  .ms-skeleton-card {
    height: 120px;
    background: linear-gradient(145deg, #d1d6df, #e0e5ec);
    border-radius: 16px;
    animation: ms-shimmer 1.4s infinite;
    background-size: 200% 100%;
  }

  @keyframes ms-shimmer {
    from { background-position: -200% 0; }
    to { background-position: 200% 0; }
  }

  /* Responsive */
  @media (max-width: 768px) {
    .ms-root {
      padding: 120px 16px 48px;
    }

    .ms-top-nav {
      padding: 10px 16px;
    }

    .ms-goto-member-btn-top {
      padding: 8px 16px;
      font-size: 13px;
    }

    .ms-tabs {
      flex-direction: column;
    }

    .ms-tab {
      justify-content: center;
    }

    .ms-user-grid {
      grid-template-columns: 1fr;
    }

    .ms-search-group {
      flex-direction: column;
    }

    .ms-modal {
      margin: 16px;
      padding: 16px;
    }
  }
`;

const MobileStoreDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("online");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [confirmModal, setConfirmModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [storeNotes, setStoreNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [processing, setProcessing] = useState(false);

  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);

  const [searchUniqueId, setSearchUniqueId] = useState("");
  const [searchedUser, setSearchedUser] = useState(null);
  const [searchingUser, setSearchingUser] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]); // Array of {productId, quantity}
  const [offlinePurchaseModal, setOfflinePurchaseModal] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    if (activeTab === "online") {
      fetchOrders();
    } else {
      fetchProducts();
    }
  }, [activeTab]);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/product/mobile-store/orders`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setOrders(response.data.orders || []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoadingProducts(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/product/available`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setProducts(response.data.products || []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch products");
    } finally {
      setLoadingProducts(false);
    }
  };

  const searchUserByUniqueId = async () => {
    if (!searchUniqueId.trim()) {
      setError("Please enter a unique ID");
      return;
    }

    setSearchingUser(true);
    setError("");
    setSearchedUser(null);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/user/by-unique-id/${searchUniqueId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setSearchedUser(response.data.user);
      setSuccess("User found successfully");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "User not found");
      setSearchedUser(null);
    } finally {
      setSearchingUser(false);
    }
  };

  const handleOfflinePurchase = async () => {
    if (!searchedUser || selectedProducts.length === 0) {
      setError("Please select a user and at least one product");
      return;
    }

    // Validate quantities
    for (const item of selectedProducts) {
      if (item.quantity < 1) {
        setError("All products must have valid quantity");
        return;
      }
    }

    setProcessing(true);
    setError("");
    setSuccess("");

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${process.env.REACT_APP_API_URL}/product/mobile-store/offline-purchase`,
        {
          userId: searchedUser._id,
          items: selectedProducts,
          invoiceNumber,
          storeNotes
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess("Offline purchase completed successfully. Commissions distributed.");
      setOfflinePurchaseModal(false);
      setSearchedUser(null);
      setSearchUniqueId("");
      setSelectedProducts([]);
      setInvoiceNumber("");
      setStoreNotes("");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to complete offline purchase");
    } finally {
      setProcessing(false);
    }
  };

  const handleAddProduct = (productId, quantity) => {
    const existingIndex = selectedProducts.findIndex(p => p.productId === productId);
    if (existingIndex >= 0) {
      // Update existing product quantity
      const updated = [...selectedProducts];
      updated[existingIndex].quantity = quantity;
      setSelectedProducts(updated);
    } else {
      // Add new product
      setSelectedProducts([...selectedProducts, { productId, quantity }]);
    }
  };

  const handleRemoveProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter(p => p.productId !== productId));
  };

  const handleProductQuantityChange = (productId, quantity) => {
    setSelectedProducts(selectedProducts.map(p => 
      p.productId === productId ? { ...p, quantity: Math.max(1, quantity) } : p
    ));
  };

  const getTotalAmount = () => {
    return selectedProducts.reduce((sum, item) => {
      const product = products.find(p => p._id === item.productId);
      return sum + (product ? product.sp * item.quantity : 0);
    }, 0);
  };

  const getTotalPv = () => {
    return selectedProducts.reduce((sum, item) => {
      const product = products.find(p => p._id === item.productId);
      return sum + (product ? product.pv * item.quantity : 0);
    }, 0);
  };

  const handleGenerateInvoice = async (orderId) => {
    setProcessing(true);
    setError("");
    setSuccess("");

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/product/mobile-store/generate-invoice/${orderId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess(`Invoice generated: ${response.data.invoiceNumber}`);
      setInvoiceNumber(response.data.invoiceNumber);
      
      setOrders(orders.map(order => 
        order._id === orderId ? { ...order, invoiceNumber: response.data.invoiceNumber } : order
      ));

      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to generate invoice");
    } finally {
      setProcessing(false);
    }
  };

  const handleViewInvoice = async (orderId) => {
    setLoadingInvoice(true);
    setError("");
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/product/invoice/${orderId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setInvoiceData(response.data.invoice);
      setShowInvoiceModal(true);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch invoice");
    } finally {
      setLoadingInvoice(false);
    }
  };

  const handlePrintInvoice = () => {
    window.print();
  };

  const handleConfirmOrder = async () => {
    if (!selectedOrder) return;

    setProcessing(true);
    setError("");
    setSuccess("");

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${process.env.REACT_APP_API_URL}/product/mobile-store/confirm-order/${selectedOrder._id}`,
        {
          invoiceNumber,
          storeNotes
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess("Order confirmed and commissions distributed successfully");
      setConfirmModal(false);
      setSelectedOrder(null);
      setInvoiceNumber("");
      setStoreNotes("");
      await fetchOrders();

      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to confirm order");
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectOrder = async () => {
    if (!selectedOrder) return;

    setProcessing(true);
    setError("");
    setSuccess("");

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${process.env.REACT_APP_API_URL}/product/mobile-store/reject-order/${selectedOrder._id}`,
        {
          rejectionReason
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess("Order rejected and refund processed successfully");
      setRejectModal(false);
      setSelectedOrder(null);
      setRejectionReason("");
      await fetchOrders();

      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to reject order");
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: "ms-badge-pending",
      processing: "ms-badge-processing",
      delivered: "ms-badge-delivered",
      cancelled: "ms-badge-cancelled"
    };
    return (
      <span className={`ms-badge ${statusStyles[status] || "ms-badge-default"}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const stats = {
    pending: orders.filter(o => o.status === "pending").length,
    delivered: orders.filter(o => o.status === "delivered").length,
    cancelled: orders.filter(o => o.status === "cancelled").length,
    total: orders.length
  };

  if (loading) {
    return (
      <div className="ms-skeleton">
        <style>{styles}</style>
        <div className="ms-skeleton-inner">
          <div className="ms-skeleton-title"></div>
          <div className="ms-skeleton-grid">
            <div className="ms-skeleton-card"></div>
            <div className="ms-skeleton-card"></div>
            <div className="ms-skeleton-card"></div>
            <div className="ms-skeleton-card"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ms-root">
      <style>{styles}</style>
      <div className="ms-top-nav">
        <button
          onClick={() => navigate("/dashboard")}
          className="ms-goto-member-btn-top"
        >
          <FiArrowLeft size={20} />
          Go to Member View
        </button>
      </div>
      <div className="ms-container">
        <div className="ms-header">
          <h1 className="ms-title">Mobile Store Dashboard</h1>
          <p className="ms-subtitle">Manage and process product orders</p>
        </div>

        {error && (
          <div className="ms-alert-error">
            <FiAlertCircle className="ms-alert-icon" size={20} />
            <p className="ms-alert-text">{error}</p>
          </div>
        )}

        {success && (
          <div className="ms-alert-success">
            <FiCheck className="ms-alert-icon" size={20} />
            <p className="ms-alert-text">{success}</p>
          </div>
        )}

        <div className="ms-tabs">
          <button
            onClick={() => setActiveTab("online")}
            className={`ms-tab ${activeTab === "online" ? "ms-tab-active" : "ms-tab-inactive"}`}
          >
            <FiShoppingBag /> Online Orders
          </button>
          <button
            onClick={() => setActiveTab("offline")}
            className={`ms-tab ${activeTab === "offline" ? "ms-tab-active" : "ms-tab-inactive"}`}
          >
            <FiPlus /> Offline Purchase
          </button>
        </div>

        {activeTab === "online" ? (
          <div>
            <div className="ms-stats-grid">
              <div className="ms-stat-card">
                <div className="ms-stat-content">
                  <div>
                    <p className="ms-stat-label">Total Orders</p>
                    <p className="ms-stat-value">{stats.total}</p>
                  </div>
                  <FiPackage className="ms-stat-icon" style={{ color: '#2563eb' }} />
                </div>
              </div>
              <div className="ms-stat-card">
                <div className="ms-stat-content">
                  <div>
                    <p className="ms-stat-label">Pending</p>
                    <p className="ms-stat-value ms-stat-value-yellow">{stats.pending}</p>
                  </div>
                  <FiClock className="ms-stat-icon" style={{ color: '#ca8a04' }} />
                </div>
              </div>
              <div className="ms-stat-card">
                <div className="ms-stat-content">
                  <div>
                    <p className="ms-stat-label">Delivered</p>
                    <p className="ms-stat-value ms-stat-value-green">{stats.delivered}</p>
                  </div>
                  <FiCheck className="ms-stat-icon" style={{ color: '#16a34a' }} />
                </div>
              </div>
              <div className="ms-stat-card">
                <div className="ms-stat-content">
                  <div>
                    <p className="ms-stat-label">Cancelled</p>
                    <p className="ms-stat-value ms-stat-value-red">{stats.cancelled}</p>
                  </div>
                  <FiX className="ms-stat-icon" style={{ color: '#dc2626' }} />
                </div>
              </div>
            </div>

            <div className="ms-table-container">
              <div className="ms-table-header">
                <h2 className="ms-table-title">Recent Orders</h2>
              </div>

              {orders.length === 0 ? (
                <div className="ms-empty-state">
                  <FiShoppingBag className="ms-empty-icon" size={48} />
                  <p className="ms-empty-text">No orders yet</p>
                </div>
              ) : (
                <div className="ms-table-wrapper">
                  <table className="ms-table">
                    <thead className="ms-table-head">
                      <tr>
                        <th className="ms-table-head-cell">Order ID</th>
                        <th className="ms-table-head-cell">Customer</th>
                        <th className="ms-table-head-cell">Product</th>
                        <th className="ms-table-head-cell">Quantity</th>
                        <th className="ms-table-head-cell">Amount</th>
                        <th className="ms-table-head-cell">Invoice</th>
                        <th className="ms-table-head-cell">Status</th>
                        <th className="ms-table-head-cell">Date</th>
                        <th className="ms-table-head-cell">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="ms-table-body">
                      {orders.map(order => (
                        <tr key={order._id}>
                          <td className="ms-table-cell">
                            {order._id.slice(-8)}
                          </td>
                          <td className="ms-table-cell">
                            <div>
                              <div className="ms-customer-name">{order.userId?.name}</div>
                              <div className="ms-customer-mobile">{order.userId?.mobile}</div>
                            </div>
                          </td>
                          <td className="ms-table-cell">
                            {order.items && order.items.length > 0 ? (
                              <div>
                                {order.items.map((item, idx) => (
                                  <div key={idx} style={{ fontSize: '12px', marginBottom: '2px' }}>
                                    {item.productName || `Product ${idx + 1}`} (Qty: {item.quantity})
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span>{order.productId?.name || 'Unknown Product'}</span>
                            )}
                          </td>
                          <td className="ms-table-cell">
                            {order.items && order.items.length > 0 
                              ? order.items.reduce((sum, item) => sum + item.quantity, 0)
                              : order.quantity}
                          </td>
                          <td className="ms-table-cell ms-table-cell-amount">
                            ₹{order.totalAmount?.toLocaleString("en-IN") || 0}
                          </td>
                          <td className="ms-table-cell">
                            {order.invoiceNumber || (
                              <span className="ms-no-invoice">Not generated</span>
                            )}
                          </td>
                          <td className="ms-table-cell">
                            {getStatusBadge(order.status)}
                          </td>
                          <td className="ms-table-cell">
                            {new Date(order.createdAt).toLocaleDateString("en-IN")}
                          </td>
                          <td className="ms-table-cell">
                            {order.status === "pending" && (
                              <div className="ms-actions">
                                {!order.invoiceNumber && (
                                  <button
                                    onClick={() => handleGenerateInvoice(order._id)}
                                    disabled={processing}
                                    className="ms-btn ms-btn-purple"
                                  >
                                    <FiPlus /> Invoice
                                  </button>
                                )}
                                <button
                                  onClick={() => {
                                    setSelectedOrder(order);
                                    setInvoiceNumber(order.invoiceNumber || "");
                                    setConfirmModal(true);
                                  }}
                                  className="ms-btn ms-btn-green"
                                >
                                  Confirm
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedOrder(order);
                                    setRejectModal(true);
                                  }}
                                  className="ms-btn ms-btn-red"
                                >
                                  Reject
                                </button>
                              </div>
                            )}
                            {(order.status === "delivered" || order.status === "processing") && (
                              <div className="ms-actions">
                                {order.invoiceNumber && (
                                  <button
                                    onClick={() => handleViewInvoice(order._id)}
                                    disabled={loadingInvoice}
                                    className="ms-btn ms-btn-blue"
                                  >
                                    <FiEye /> View Invoice
                                  </button>
                                )}
                                <span className="ms-completed">Completed</span>
                              </div>
                            )}
                            {order.status === "cancelled" && (
                              <span className="ms-cancelled-text">Cancelled</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="ms-offline-container">
            <h2 className="ms-offline-title">Offline Purchase</h2>
            
            <div className="ms-search-section">
              <h3 className="ms-search-title">
                <FiUser /> Search User by Unique ID
              </h3>
              <div className="ms-search-group">
                <input
                  type="text"
                  value={searchUniqueId}
                  onChange={(e) => setSearchUniqueId(e.target.value)}
                  className="ms-search-input"
                  placeholder="Enter user's unique ID (e.g., ABM123456)"
                />
                <button
                  onClick={searchUserByUniqueId}
                  disabled={searchingUser}
                  className="ms-search-btn"
                >
                  <FiSearch />
                  {searchingUser ? "Searching..." : "Search"}
                </button>
              </div>
            </div>

            {searchedUser && (
              <div className="ms-user-details">
                <h3 className="ms-user-details-title">User Details</h3>
                <div className="ms-user-grid">
                  <p><strong>Name:</strong> {searchedUser.name}</p>
                  <p><strong>Unique ID:</strong> {searchedUser.uniqueId}</p>
                  <p><strong>Mobile:</strong> {searchedUser.mobile}</p>
                  <p><strong>Email:</strong> {searchedUser.email}</p>
                  <p><strong>PUAP Wallet:</strong> ₹{Number(searchedUser.puapWallet || 0).toLocaleString("en-IN")}</p>
                  <p><strong>Total PV:</strong> {searchedUser.totalPvPurchased || 0}</p>
                </div>
                <button
                  onClick={() => setOfflinePurchaseModal(true)}
                  className="ms-purchase-btn"
                >
                  <FiPlus /> Create Purchase
                </button>
              </div>
            )}

            {!searchedUser && (
              <div className="ms-products-section">
                <h3 className="ms-products-title">Available Products</h3>
                {loadingProducts ? (
                  <div className="ms-loading-text">Loading products...</div>
                ) : products.length === 0 ? (
                  <div className="ms-loading-text">No products available</div>
                ) : (
                  <div className="ms-products-grid">
                    {products.map(product => (
                      <div key={product._id} className="ms-product-card">
                        <h4 className="ms-product-name">{product?.name}</h4>
                        <p className="ms-product-detail">MRP: ₹{product.mrp}</p>
                        <p className="ms-product-detail">SP: ₹{product.sp}</p>
                        <p className="ms-product-detail">PV: {product.pv}</p>
                        <p className="ms-product-detail">Stock: {product.quantity}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirm Order Modal */}
      {confirmModal && selectedOrder && (
        <div className="ms-modal-overlay">
          <div className="ms-modal">
            <button
              className="ms-modal-close"
              onClick={() => {
                setConfirmModal(false);
                setSelectedOrder(null);
                setInvoiceNumber("");
                setStoreNotes("");
              }}
            >
              &times;
            </button>
            <h3 className="ms-modal-title ms-modal-title-confirm">Confirm Order</h3>
            
            <div className="ms-modal-details">
              {selectedOrder.items && selectedOrder.items.length > 0 ? (
                <div>
                  <p><strong>Products:</strong></p>
                  {selectedOrder.items.map((item, idx) => (
                    <p key={idx} style={{ marginLeft: '12px' }}>
                      • {item.productName || `Product ${idx + 1}`} (Qty: {item.quantity}) - ₹{item.totalAmount?.toLocaleString("en-IN") || 0}
                    </p>
                  ))}
                  <p><strong>Total Amount:</strong> ₹{selectedOrder.totalAmount?.toLocaleString("en-IN") || 0}</p>
                  <p><strong>Total PV:</strong> {selectedOrder.totalPv || 0}</p>
                </div>
              ) : (
                <div>
                  <p><strong>Product:</strong> {selectedOrder.productId?.name || 'Unknown Product'}</p>
                  <p><strong>Quantity:</strong> {selectedOrder.quantity}</p>
                  <p><strong>Amount:</strong> ₹{selectedOrder.totalAmount?.toLocaleString("en-IN") || 0}</p>
                </div>
              )}
              <p><strong>Customer:</strong> {selectedOrder.userId?.name} ({selectedOrder.userId?.mobile})</p>
              <p><strong>Delivery Address:</strong> {selectedOrder.deliveryAddress}</p>
              <p><strong>Contact:</strong> {selectedOrder.deliveryContact}</p>
            </div>

            <div>
              <div className="ms-form-group">
                <label className="ms-form-label">Invoice Number</label>
                <input
                  type="text"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  className="ms-form-input"
                  placeholder="Enter invoice number"
                />
              </div>
              <div className="ms-form-group">
                <label className="ms-form-label">Store Notes (Optional)</label>
                <textarea
                  value={storeNotes}
                  onChange={(e) => setStoreNotes(e.target.value)}
                  rows="2"
                  className="ms-form-textarea"
                  placeholder="Any delivery notes"
                />
              </div>
              <button
                onClick={handleConfirmOrder}
                disabled={processing}
                className="ms-modal-btn ms-modal-btn-green"
              >
                {processing ? "Processing..." : "Confirm Order & Distribute Commissions"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Order Modal */}
      {rejectModal && selectedOrder && (
        <div className="ms-modal-overlay">
          <div className="ms-modal">
            <button
              className="ms-modal-close"
              onClick={() => {
                setRejectModal(false);
                setSelectedOrder(null);
                setRejectionReason("");
              }}
            >
              &times;
            </button>
            <h3 className="ms-modal-title ms-modal-title-reject">Reject Order</h3>
            
            <div className="ms-modal-details">
              {selectedOrder.items && selectedOrder.items.length > 0 ? (
                <div>
                  <p><strong>Products:</strong></p>
                  {selectedOrder.items.map((item, idx) => (
                    <p key={idx} style={{ marginLeft: '12px' }}>
                      • {item.productName || `Product ${idx + 1}`} (Qty: {item.quantity})
                    </p>
                  ))}
                  <p><strong>Total Amount:</strong> ₹{selectedOrder.totalAmount?.toLocaleString("en-IN") || 0}</p>
                </div>
              ) : (
                <div>
                  <p><strong>Product:</strong> {selectedOrder.productId?.name || 'Unknown Product'}</p>
                  <p><strong>Quantity:</strong> {selectedOrder.quantity}</p>
                  <p><strong>Amount:</strong> ₹{selectedOrder.totalAmount?.toLocaleString("en-IN") || 0}</p>
                </div>
              )}
              <p><strong>Customer:</strong> {selectedOrder.userId?.name} ({selectedOrder.userId?.mobile})</p>
            </div>

            <div>
              <div className="ms-form-group">
                <label className="ms-form-label">Rejection Reason *</label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows="2"
                  className="ms-form-textarea"
                  placeholder="Please provide a reason for rejection"
                  required
                />
              </div>
              <p className="ms-rejection-note">
                <strong>Note:</strong> Rejecting this order will refund the full amount to the customer's wallet.
              </p>
              <button
                onClick={handleRejectOrder}
                disabled={processing || !rejectionReason}
                className="ms-modal-btn ms-modal-btn-red"
              >
                {processing ? "Processing..." : "Reject & Refund"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Offline Purchase Modal */}
      {offlinePurchaseModal && searchedUser && (
        <div className="ms-modal-overlay">
          <div className="ms-modal">
            <button
              className="ms-modal-close"
              onClick={() => {
                setOfflinePurchaseModal(false);
                setSelectedProducts([]);
                setInvoiceNumber("");
                setStoreNotes("");
              }}
            >
              &times;
            </button>
            <h3 className="ms-modal-title ms-modal-title-confirm">Create Offline Purchase</h3>
            
            <div className="ms-modal-details">
              <p><strong>Customer:</strong> {searchedUser.name}</p>
              <p><strong>Unique ID:</strong> {searchedUser.uniqueId}</p>
              <p><strong>PUAP Wallet:</strong> ₹{Number(searchedUser.puapWallet || 0).toLocaleString("en-IN")}</p>
            </div>

            <div>
              <div className="ms-form-group">
                <label className="ms-form-label">Add Product *</label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <select
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        handleAddProduct(e.target.value, 1);
                        e.target.value = "";
                      }
                    }}
                    className="ms-form-select"
                    style={{ flex: 1 }}
                  >
                    <option value="">-- Select Product --</option>
                    {products.filter(p => p.quantity > 0).map(product => (
                      <option key={product._id} value={product._id}>
                        {product?.name} - ₹{product.sp} (PV: {product.pv})
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      const select = document.querySelector('.ms-form-select');
                      if (select && select.value) {
                        handleAddProduct(select.value, 1);
                        select.value = "";
                      }
                    }}
                    className="ms-btn ms-btn-blue"
                    style={{ padding: '10px 16px' }}
                  >
                    <FiPlus /> Add
                  </button>
                </div>
              </div>

              {selectedProducts.length > 0 && (
                <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(37, 99, 235, 0.05)', borderRadius: '8px' }}>
                  <p style={{ fontWeight: '700', marginBottom: '8px', color: '#1e293b' }}>Selected Products:</p>
                  {selectedProducts.map((item) => {
                    const product = products.find(p => p._id === item.productId);
                    if (!product || !product?.name) return null;
                    return (
                      <div key={item.productId} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', padding: '8px', background: '#fff', borderRadius: '6px' }}>
                        <div style={{ flex: 1 }}>
                          <p style={{ fontWeight: '600', margin: 0 }}>{product?.name}</p>
                          <p style={{ fontSize: '12px', color: '#64748b', margin: '2px 0' }}>₹{product?.sp} | PV: {product?.pv}</p>
                        </div>
                        <input
                          type="number"
                          min="1"
                          max={product.quantity}
                          value={item.quantity}
                          onChange={(e) => handleProductQuantityChange(item.productId, parseInt(e.target.value) || 1)}
                          className="ms-form-input"
                          style={{ width: '70px', padding: '6px 10px' }}
                        />
                        <button
                          onClick={() => handleRemoveProduct(item.productId)}
                          className="ms-btn ms-btn-red"
                          style={{ padding: '6px 10px' }}
                        >
                          <FiX />
                        </button>
                      </div>
                    );
                  })}
                  <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                    <p style={{ fontWeight: '700', margin: '4px 0' }}>Total Amount: ₹{getTotalAmount().toLocaleString("en-IN")}</p>
                    <p style={{ fontWeight: '700', margin: '4px 0' }}>Total PV: {getTotalPv()}</p>
                  </div>
                </div>
              )}

              <div className="ms-form-group">
                <label className="ms-form-label">Invoice Number</label>
                <input
                  type="text"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  className="ms-form-input"
                  placeholder="Enter invoice number"
                />
              </div>
              <div className="ms-form-group">
                <label className="ms-form-label">Store Notes (Optional)</label>
                <textarea
                  value={storeNotes}
                  onChange={(e) => setStoreNotes(e.target.value)}
                  rows="2"
                  className="ms-form-textarea"
                  placeholder="Any notes"
                />
              </div>
              <button
                onClick={handleOfflinePurchase}
                disabled={processing || selectedProducts.length === 0}
                className="ms-modal-btn ms-modal-btn-green"
              >
                {processing ? "Processing..." : `Complete Purchase (${selectedProducts.length} items)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {showInvoiceModal && invoiceData && (
        <Invoice
          invoice={invoiceData}
          onClose={() => {
            setShowInvoiceModal(false);
            setInvoiceData(null);
          }}
          onPrint={handlePrintInvoice}
        />
      )}
    </div>
  );
};

export default MobileStoreDashboard;